/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import Dropzone, { Accept } from 'react-dropzone';
import { IEveOrderUploadData } from './eve-order-upload.data';
import { IEveOrderUploadProps } from './eve-order-upload.props.autogenerated';
import readXlsxFile, { Row } from 'read-excel-file';
import { searchAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { EveOrderUploadStorageInput, IEveOrderUploadStorageData } from '../../actions/eve-order-upload-storage.action';
import { uniqBy } from 'lodash';
import { EveQuickOrderTabInput, IEveQuickOrderTabData } from '../../actions/eve-quick-order-tab.action';
import { EveQuickOrderItemsInput, IEveQuickOrderItemsData } from '../../actions/eve-quick-order-items.action';

export interface IEveOrderUploadExtendedProps<T> extends IEveOrderUploadProps<T> {
    closeQuickOrder: () => void;
}

export interface IEveOrderUploadViewProps extends IEveOrderUploadProps<IEveOrderUploadData> {
    importLogger: React.ReactNode;
    dropzone: React.ReactNode;
}

interface IEveOrderUploadState {
    isImporting: boolean;
    isProcessing: boolean;
    showTooltip: boolean;
    uploadedFile?: any;
}

enum ErrorType {
    invalidFileType = 'file-invalid-type'
}

enum MimeType {
    CSV = 'text/csv',
    XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    XLS = 'application/vnd.ms-excel' // Firefox use this mime for CSV files
}

/**
 *
 * OrderUpload component
 * @extends {React.PureComponent<IEveOrderUploadProps<IEveOrderUploadData>>}
 */
class EveOrderUpload extends React.PureComponent<IEveOrderUploadExtendedProps<IEveOrderUploadData>, IEveOrderUploadState> {
    private readonly baseProps = { 'data-ignore-parent-css': true }; // This should be passed to every jsx element
    private readonly tooltipContentRef = React.createRef<HTMLDivElement>();

    constructor(props: IEveOrderUploadExtendedProps<IEveOrderUploadData>) {
        super(props);

        const { data } = this.props;
        const { orderUploadStorage } = data;

        this.state = {
            isImporting: false,
            showTooltip: false,
            isProcessing:
                !!orderUploadStorage.result?.filename &&
                !!orderUploadStorage.result.items &&
                !orderUploadStorage.result.items.some(({ variants }) => !!variants)
        };

        this._handleGoToManual = this._handleGoToManual.bind(this);
        this._handleImport = this._handleImport.bind(this);
        this._handleReturn = this._handleReturn.bind(this);
        this._isValidFile = this._isValidFile.bind(this);
        this._parseFile = this._parseFile.bind(this);
        this._renderDataFormatDescription = this._renderDataFormatDescription.bind(this);
        this._renderDropzone = this._renderDropzone.bind(this);
        this._searchProducts = this._searchProducts.bind(this);
        this._isImported = this._isImported.bind(this);
    }

    private readonly _cn = (suffix: string) => {
        return `cps-order-upload${suffix}`;
    };

    /**
     * Check for touchscreen device
     */
    private readonly _hasTouchSupport = () => {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };

    private readonly _parseFile = async (file: File) => {
        const { context, config } = this.props;
        const { actionContext } = context;
        const { maxItems } = config;
        const assertCSV = (value: string | ArrayBuffer): value is string => file.type !== MimeType.XLSX;
        const isCSV = file.type !== MimeType.XLSX;
        const raw = await file[isCSV ? 'text' : 'arrayBuffer']();
        const normalized: Row[] = (assertCSV(raw) ? raw.split(/\n|\r|\r\n/).map(row => row.split(',')) : await readXlsxFile(raw)).slice(
            0,
            maxItems
        );
        const rows = normalized.map(([c0, c1, c2, c3, ...rest]) => [`${c0}`, `${c1}`, `${c2}`, parseInt(c3 as string, 10), ...rest]);
        const acceptedRows = rows
            .filter(
                cells =>
                    cells.length === 4 &&
                    !isNaN(cells[3] as number) && // last cell value must not be NaN
                    ![cells[0], cells[1], cells[2]].some(val => typeof val !== 'string' || val.length === 0) // 1st 3 cells must be string and must not be empty
            )
            .map(cells => ({
                itemNumber: (cells[0] as string).trim(),
                configuration: (cells[1] as string).trim(),
                source: (cells[2] as string).trim(),
                notFound: false,
                quantity: cells[3] as number
            }));
        const rejectedRows = rows
            .filter(
                cells =>
                    cells.length !== 4 ||
                    isNaN(cells[3] as number) ||
                    [cells[0], cells[1], cells[2]].some(val => typeof val !== 'string' || val.length === 0)
            )
            .map(cells => ({
                itemNumber: cells[0],
                configuration: cells[1],
                source: cells[2],
                quantity: cells[3]
            }));
        actionContext.update<IEveOrderUploadStorageData>(new EveOrderUploadStorageInput(), {
            filename: file.name,
            items: acceptedRows,
            rejectedItems: rejectedRows
        });
        return { acceptedRows, rejectedRows };
    };

    private readonly _isValidFile = (file: File) => {
        let result = true;
        try {
            result = ([MimeType.CSV, MimeType.XLSX, MimeType.XLS] as string[]).includes(file.type);
        } catch {
            result = false;
        }
        return result;
    };

    /**
     * Process dropped files
     * Currently support only 1 file at a time due to app performance
     * @param files
     */
    private readonly _processFiles = async (files: File[]): Promise<void> => {
        this.setState({ isProcessing: true });
        const { actionContext } = this.props.context;
        const [file] = files;
        if (this._isValidFile(file)) {
            const { acceptedRows, rejectedRows } = await this._parseFile(file);
            const searchResults = await this._searchProducts(acceptedRows);
            this.setState({ isProcessing: false });
            actionContext.update<IEveOrderUploadStorageData>(new EveOrderUploadStorageInput(), {
                filename: file.name,
                items: acceptedRows.map(({ configuration, itemNumber, quantity, source }) => {
                    const { itemNumber: _id, name, variants } = searchResults.filter(result => result.itemNumber === itemNumber)[0] ?? {};
                    const found =
                        _id?.length && variants?.some(({ ColorId, ConfigId }) => ConfigId === configuration && ColorId === source);
                    return {
                        configuration,
                        itemNumber,
                        name,
                        quantity,
                        notFound: !found,
                        source,
                        variants
                    };
                }),
                itemsFoundOnSearch: acceptedRows
                    .filter(({ itemNumber, configuration, source }) =>
                        searchResults.some(
                            result =>
                                result.itemNumber === itemNumber &&
                                result.variants?.some(({ ColorId, ConfigId }) => ColorId === source && ConfigId === configuration)
                        )
                    )
                    .map(({ configuration, itemNumber, quantity, source }) => {
                        const { selectedVariant, name, variants } =
                            searchResults
                                .filter(
                                    result =>
                                        result.itemNumber === itemNumber &&
                                        result.variants?.some(({ ColorId, ConfigId }) => ConfigId === configuration && ColorId === source)
                                )
                                .map(result => ({
                                    ...result,
                                    selectedVariant: result.variants?.filter(
                                        ({ ColorId, ConfigId }) => ConfigId === configuration && ColorId === source
                                    )[0]
                                }))[0] ?? {};
                        return {
                            configuration,
                            itemNumber,
                            name,
                            quantity,
                            selectedVariant,
                            source,
                            variants
                        };
                    }),
                rejectedItems: rejectedRows
            });
        }
    };

    private readonly _searchProducts = async (
        inputs: {
            itemNumber: string;
            configuration: string;
            source: string;
            quantity: number;
        }[]
    ) => {
        const { context, config } = this.props;
        const { maxItems = 100 } = config;
        const { actionContext, request } = context;
        const { apiSettings } = request;
        const { channelId, catalogId } = apiSettings;
        const ItemIds = uniqBy(inputs, 'itemNumber')
            // @ts-expect-error
            .map(({ itemNumber }) => ({ ItemId: itemNumber }))
            .slice(0, maxItems);
        const max = (ItemIds.length < maxItems ? ItemIds.length : maxItems) * 3;
        const result = await searchAsync(
            {
                callerContext: actionContext,
                queryResultSettings: { Paging: { Top: max < 1000 ? max : 1000 } }
            },
            // @ts-expect-error
            { ItemIds, Context: { CatalogId: catalogId, ChannelId: channelId } }
        );
        return result.map(({ ItemId, CompositionInformation, ProductName }) => ({
            itemNumber: ItemId,
            name: ProductName,
            variants: CompositionInformation?.VariantInformation?.Variants
        }));
    };

    private readonly _handleReturn = () => {
        const { actionContext } = this.props.context;
        actionContext.update<IEveOrderUploadStorageData>(new EveOrderUploadStorageInput(), {});
    };

    private readonly _handleGoToManual = () => {
        const { actionContext } = this.props.context;
        actionContext.update<IEveQuickOrderTabData>(new EveQuickOrderTabInput(), { currentTab: 'manual' });
    };

    private readonly _handleImport = () => {
        this.setState({ isImporting: true });
        const { context, data } = this.props;
        const { quickOrderItems, orderUploadStorage } = data;
        const { actionContext } = context;
        const { items = [] } = quickOrderItems.result ?? {};
        const trimmedItems = items.slice();
        let lastFilledItemIndex = 0;
        for (let i = items.length - 1; i > 0; i--) {
            if (items[`${i}`].itemNumber.length) {
                lastFilledItemIndex = i;
                break;
            }
        }
        trimmedItems.splice(lastFilledItemIndex === 0 ? lastFilledItemIndex : lastFilledItemIndex + 1);
        const { itemsFoundOnSearch = [] } = orderUploadStorage.result ?? {};
        const itemsToAdd: IEveQuickOrderItemsData['items'] = itemsFoundOnSearch.map(
            ({ itemNumber, quantity, name, selectedVariant, variants }) => ({
                itemNumber,
                quantity,
                description: { name },
                selectedVariant,
                selectedVariantID: selectedVariant?.VariantId,
                variants
            })
        );
        const finalItems = [...trimmedItems, ...itemsToAdd];
        actionContext.update<IEveQuickOrderItemsData>(new EveQuickOrderItemsInput(), { items: finalItems });
        setTimeout(() => {
            this.setState({ isImporting: false });
        }, 1000);
    };

    private readonly _isImported = () => {
        const { quickOrderItems, orderUploadStorage } = this.props.data;
        const idsInQuickOrder = quickOrderItems.result?.items.map(({ selectedVariantID }) => selectedVariantID).filter(id => !!id) ?? [];
        const idsInOrderUpload =
            orderUploadStorage.result?.itemsFoundOnSearch?.map(({ selectedVariant }) => selectedVariant?.VariantId).filter(id => !!id) ??
            [];
        return (
            idsInQuickOrder.length && idsInOrderUpload.length && !idsInOrderUpload.map(id => idsInQuickOrder.includes(id)).some(val => !val)
        );
    };

    private readonly _renderImportLogger = () => {
        const { data, resources } = this.props;
        const { orderUploadStorage } = data;
        const {
            importLoggerGoToManualButton,
            importLoggerHeader,
            importLoggerImportButton,
            importLoggerImportedButton,
            importLoggerImportingButton,
            importLoggerReturnButton
        } = resources;

        return (
            <div {...this.baseProps} className={this._cn('__logger')}>
                <div {...this.baseProps} className={this._cn('__logger--wrapper')}>
                    <div {...this.baseProps} className={this._cn('__logger--header')}>
                        <div {...this.baseProps} className={this._cn('__logger--header-title')}>
                            {importLoggerHeader}
                        </div>
                        <div {...this.baseProps} className={this._cn('__logger--header-filename')}>
                            {`${orderUploadStorage.result?.filename} - Total: ${orderUploadStorage.result?.items?.length} - Valid: ${
                                this.state.isProcessing ? '...' : orderUploadStorage.result?.itemsFoundOnSearch?.length ?? 0
                            }`}
                        </div>
                    </div>
                    <div {...this.baseProps} className={this._cn('__logger--body')}>
                        <div {...this.baseProps} className={this._cn('__logger--item-wrapper')}>
                            {orderUploadStorage.result?.items?.map(({ notFound, name, itemNumber, configuration, source, quantity }, i) => (
                                <div
                                    {...this.baseProps}
                                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                                    key={itemNumber + i.toString()}
                                    className={`${this._cn('__logger--item')} ${notFound ? 'not-found' : ''}`}
                                >
                                    {itemNumber}
                                    {this.state.isProcessing ? (
                                        <span
                                            {...this.baseProps}
                                            className={`${this._cn('__logger--item-name')} loading${!name?.length ? '' : ' found'}`}
                                        >
                                            {name?.length ? `| ${name}` : ''}
                                        </span>
                                    ) : (
                                        ` | ${name} `
                                    )}
                                    {configuration.length && !/^\.$/.test(configuration.trim()) ? `| ${configuration}` : ''}
                                    {source.length && !/^\.$/.test(source.trim()) ? `| ${source}` : ''} | {quantity}
                                </div>
                            ))}
                        </div>
                        <div {...this.baseProps} className={this._cn('__logger--controls')}>
                            {([
                                [
                                    this._handleImport,
                                    this.state.isImporting
                                        ? importLoggerImportingButton
                                        : !this.state.isImporting && !this.state.isProcessing && this._isImported()
                                        ? importLoggerImportedButton
                                        : importLoggerImportButton,
                                    this._cn('__logger--import-button')
                                ],
                                [this._handleGoToManual, importLoggerGoToManualButton, this._cn('__logger--go-manual-button')],
                                [this._handleReturn, importLoggerReturnButton, this._cn('__logger--return-button')]
                            ] as const).map(([handleClick, label, className], i) => (
                                <button
                                    disabled={i === 0 && (this.state.isImporting || (!this.state.isImporting && this.state.isProcessing))}
                                    key={label}
                                    {...this.baseProps}
                                    onClick={handleClick}
                                    className={`${className}${
                                        i === 0 && (this.state.isImporting || (!this.state.isImporting && this.state.isProcessing))
                                            ? ' disabled'
                                            : ''
                                    }`}
                                >
                                    {label}
                                    {i === 0 && this.state.isImporting ? (
                                        <svg
                                            {...this.baseProps}
                                            className={this._cn('__logger-button-loader')}
                                            fill='yellow'
                                            viewBox='0 0 512 512'
                                        >
                                            <path
                                                {...this.baseProps}
                                                d='M288 39.1v16.7c0 10.8 7.3 20.2 17.7 23.1C383.2 100.4 440 171.5 440 256c0 101.7-82.3 184-184 184-101.7 0-184-82.3-184-184 0-84.5 56.8-155.6 134.3-177.2C216.7 75.9 224 66.5 224 55.7V39.1c0-15.7-14.8-27.2-30-23.2C86.6 43.5 7.4 141.2 8 257.3c.7 137.1 111.5 247 248.5 246.7C393.3 503.7 504 392.8 504 256c0-115.6-79.1-212.8-186.2-240.2C302.7 11.9 288 23.5 288 39.1z'
                                            />
                                        </svg>
                                    ) : null}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    private readonly _renderDataFormatDescription = () => {
        const { resources, config } = this.props;
        const { csvSampleURL, xlsxSampleURL } = config;
        const { tooltipDataFormat, tooltipDownloadTitle, tooltipTitle } = resources;
        return (
            <>
                <button
                    {...this.baseProps}
                    className={this._cn('__dropzone-body--tooltip-trigger')}
                    onClick={event => {
                        event.stopPropagation();
                        this.setState(state => ({ ...state, showTooltip: !state.showTooltip }));
                    }}
                >
                    <svg {...this.baseProps} fill='white' viewBox='0 0 192 512'>
                        <path
                            {...this.baseProps}
                            d='M20 424.2h20V279.8H20c-11 0-20-9-20-20V212c0-11 9-20 20-20h112c11 0 20 9 20 20v212.2h20c11 0 20 9 20 20V492c0 11-9 20-20 20H20c-11 0-20-9-20-20v-47.8c0-11 9-20 20-20zM96 0C56.2 0 24 32.2 24 72s32.2 72 72 72 72-32.2 72-72S135.8 0 96 0z'
                        />
                    </svg>
                </button>
                <div
                    {...this.baseProps}
                    ref={this.tooltipContentRef}
                    className={`${this._cn('__dropzone-body--tooltip-content')}${this.state.showTooltip ? ' show-from-top' : ''}`}
                    onClick={event => event.stopPropagation()}
                >
                    <div {...this.baseProps}>{tooltipTitle}</div>
                    <div {...this.baseProps}>{tooltipDataFormat}</div>
                    <div {...this.baseProps} className={this._cn('__dropzone-body--tooltip-sample-download')}>
                        <div {...this.baseProps}>{tooltipDownloadTitle}</div>
                        <div {...this.baseProps}>
                            <a
                                {...this.baseProps}
                                {...(csvSampleURL?.destinationUrl.length ? { href: csvSampleURL.destinationUrl } : {})}
                                tabIndex={0}
                                download
                            >
                                CSV
                            </a>
                            <a
                                {...this.baseProps}
                                {...(xlsxSampleURL?.destinationUrl.length ? { href: xlsxSampleURL.destinationUrl } : {})}
                                tabIndex={0}
                                download
                            >
                                XLSX
                            </a>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    private readonly _renderDropzone = () => {
        const acceptLavidge: Accept = {
            [MimeType.CSV]: ['.csv']
        };
        const accept: Accept = {
            [MimeType.CSV]: ['.csv'],
            [MimeType.XLSX]: ['.xlsx']
        };
        const { closeQuickOrder, resources, config, context } = this.props;
        const { layout, csvSampleURL } = config;
        const {
            dropzoneDesktopDescription,
            dropzoneDesktopHeader,
            dropzoneFileTypeError,
            dropzoneMobileDescription,
            dropzoneMobileHeader,
            dropzoneMultipleFileError,
            dropzoneOpenFileButtonText,
            dropzoneOr,
            importLoggerImportButton,
            importLoggerImportedButton,
            importLoggerImportingButton
        } = resources;

        if (layout === 'lavidge') {
            return (
                <div {...this.baseProps} className={this._cn('__dropzone--lavidge')}>
                    <div {...this.baseProps}>
                        <div {...this.baseProps}>CSV files should be formatted as follows:</div>
                        <div {...this.baseProps}>Item Number,Configuration,Source,Quantity</div>
                        <div {...this.baseProps}>Item Number,Configuration,Source,Quantity</div>
                    </div>
                    <Dropzone
                        {...this.baseProps}
                        onDropAccepted={files => {
                            this.setState({ uploadedFile: files });
                            context.actionContext.update<IEveOrderUploadStorageData>(new EveOrderUploadStorageInput(), { items: [] });
                        }}
                        noClick={true}
                        accept={acceptLavidge}
                        multiple={false}
                    >
                        {({ getRootProps, fileRejections, getInputProps, acceptedFiles }) => (
                            <div {...this.baseProps} {...getRootProps()} onClick={() => this.setState({ showTooltip: false })}>
                                <div {...this.baseProps}>
                                    <div {...this.baseProps}>
                                        <svg {...this.baseProps} fill='currentColor' viewBox='0 0 384 512'>
                                            <path
                                                {...this.baseProps}
                                                d='M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm65.2 216H224v80c0 8.8-7.2 16-16 16h-32c-8.8 0-16-7.2-16-16v-80H94.8c-14.3 0-21.4-17.3-11.3-27.4l96.4-95.7c6.7-6.6 17.4-6.6 24 0l96.4 95.7c10.2 10.1 3 27.4-11.3 27.4zM377 105L279.1 7c-4.5-4.5-10.6-7-17-7H256v128h128v-6.1c0-6.3-2.5-12.4-7-16.9z'
                                            />
                                        </svg>
                                    </div>
                                    <div {...this.baseProps}>
                                        Drag and drop a CSV file here or{' '}
                                        <label {...this.baseProps} htmlFor={this._cn('__dropzone-body--open-dialog-button')}>
                                            choose file
                                        </label>
                                        .
                                        <input {...getInputProps()} id={this._cn('__dropzone-body--open-dialog-button')} />
                                        <div {...this.baseProps}>
                                            {!fileRejections.length && acceptedFiles.length ? acceptedFiles[0].name : ''}
                                            <span {...this.baseProps}>
                                                {fileRejections.length === 1
                                                    ? fileRejections[0].errors[0].code === ErrorType.invalidFileType
                                                        ? 'Only support .csv file'
                                                        : fileRejections[0].errors[0].message
                                                    : fileRejections.length > 1
                                                    ? dropzoneMultipleFileError
                                                    : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Dropzone>
                    <div {...this.baseProps}>
                        <button
                            {...this.baseProps}
                            onClick={async () => {
                                if (this.state.uploadedFile?.length) {
                                    this.setState({ isImporting: true });
                                    await this._processFiles(this.state.uploadedFile);
                                    this._handleImport();
                                }
                            }}
                        >
                            {this.state.isImporting
                                ? importLoggerImportingButton
                                : !this.state.isImporting && !this.state.isProcessing && this._isImported()
                                ? importLoggerImportedButton
                                : importLoggerImportButton}
                        </button>
                    </div>
                    <div {...this.baseProps}>
                        Download{' '}
                        <a {...this.baseProps} href={csvSampleURL?.destinationUrl} download>
                            an example
                        </a>{' '}
                        here
                    </div>
                </div>
            );
        }

        return (
            <Dropzone {...this.baseProps} onDrop={this._processFiles} noClick={true} accept={accept} multiple={false}>
                {({ getRootProps, isDragAccept, open, fileRejections, getInputProps }) => (
                    <div
                        {...this.baseProps}
                        {...getRootProps()}
                        className={`${this._cn('__dropzone')}${isDragAccept ? ` ${this._cn('__dropzone--accepted')}` : ''}`}
                        onClick={() => this.setState({ showTooltip: false })}
                    >
                        <div {...this.baseProps} className={this._cn('__dropzone-wrapper')}>
                            <div {...this.baseProps} className={this._cn('__dropzone-inner-wrapper')}>
                                {window.innerWidth > 992 ? null : (
                                    <button {...this.baseProps} className={this._cn('__dropzone--close-button')} onClick={closeQuickOrder}>
                                        <svg {...this.baseProps} viewBox='0 0 352 512'>
                                            <path
                                                {...this.baseProps}
                                                d='M242.7 256l100.1-100.1c12.3-12.3 12.3-32.2 0-44.5l-22.2-22.2c-12.3-12.3-32.2-12.3-44.5 0L176 189.3 75.9 89.2c-12.3-12.3-32.2-12.3-44.5 0L9.2 111.5c-12.3 12.3-12.3 32.2 0 44.5L109.3 256 9.2 356.1c-12.3 12.3-12.3 32.2 0 44.5l22.2 22.2c12.3 12.3 32.2 12.3 44.5 0L176 322.7l100.1 100.1c12.3 12.3 32.2 12.3 44.5 0l22.2-22.2c12.3-12.3 12.3-32.2 0-44.5L242.7 256z'
                                            />
                                        </svg>
                                    </button>
                                )}
                                <div {...this.baseProps} className={this._cn('__dropzone-upload-icon')}>
                                    <svg {...this.baseProps} viewBox='0 0 384 512'>
                                        <path
                                            {...this.baseProps}
                                            d='M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm65.2 216H224v80c0 8.8-7.2 16-16 16h-32c-8.8 0-16-7.2-16-16v-80H94.8c-14.3 0-21.4-17.3-11.3-27.4l96.4-95.7c6.7-6.6 17.4-6.6 24 0l96.4 95.7c10.2 10.1 3 27.4-11.3 27.4zM377 105L279.1 7c-4.5-4.5-10.6-7-17-7H256v128h128v-6.1c0-6.3-2.5-12.4-7-16.9z'
                                        />
                                    </svg>
                                </div>
                                <div {...this.baseProps} className={this._cn('__dropzone-header')}>
                                    {this._hasTouchSupport() ? dropzoneMobileHeader : dropzoneDesktopHeader}
                                </div>
                                <div {...this.baseProps} className={this._cn('__dropzone-body')}>
                                    <div {...this.baseProps} className={this._cn('__dropzone-body--description')}>
                                        {this._hasTouchSupport() ? (
                                            <>
                                                {dropzoneMobileDescription}
                                                {this._renderDataFormatDescription()}
                                            </>
                                        ) : (
                                            <>
                                                {dropzoneDesktopDescription}
                                                {this._renderDataFormatDescription()}
                                            </>
                                        )}
                                    </div>
                                    <div {...this.baseProps} className={this._cn('__dropzone-body--info-section')}>
                                        {this._hasTouchSupport() ? null : <div {...this.baseProps}>{dropzoneOr}</div>}
                                    </div>
                                    <div {...this.baseProps} className={this._cn('__dropzone-body--button-wrapper')}>
                                        <label
                                            {...this.baseProps}
                                            htmlFor={this._cn('__dropzone-body--open-dialog-button')}
                                            className={this._cn('__dropzone-body--open-dialog-button')}
                                        >
                                            {dropzoneOpenFileButtonText}
                                        </label>
                                        <input {...getInputProps()} id={this._cn('__dropzone-body--open-dialog-button')} />
                                        {fileRejections.length ? (
                                            <div {...this.baseProps} className={this._cn('__dropzone-body--error')}>
                                                {fileRejections.length === 1
                                                    ? fileRejections[0].errors[0].code === ErrorType.invalidFileType
                                                        ? dropzoneFileTypeError
                                                        : fileRejections[0].errors[0].message
                                                    : dropzoneMultipleFileError}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Dropzone>
        );
    };

    public readonly render = () => {
        const viewProps: IEveOrderUploadViewProps = {
            ...this.props,
            importLogger: this._renderImportLogger(),
            dropzone: this._renderDropzone()
        };
        return this.props.renderView(viewProps);
    };
}

export default EveOrderUpload;
